import React from "react";
import { useAuth, hasMinRole } from "@/context/AuthContext";
import { PageHeader, Card } from "@/components/common";
import {
  Rocket, ShieldCheck, LayoutDashboard, FileText, Search, ClipboardList,
  BarChart3, Landmark, Bell, FileBarChart, Users, Database, ScrollText,
  Activity, Building2, Layers, Star, GitPullRequestArrow, HelpCircle,
} from "lucide-react";

const ROLES = [
  ["Viewer", "Read-only staff", "Browse bills, search, view the dashboard, and see assignments and alerts."],
  ["Policy Analyst", "Works on bills", "Everything a Viewer can, plus record activity, write internal analysis, and open Reports."],
  ["Manager", "Leads a team", "Everything an Analyst can, plus assign bills to staff, create alert rules, and open Administration."],
  ["Administrator", "Runs the system", "Full control: manage users, data sources, organization settings, and audit logs."],
];

function Section({ id, icon: Icon, title, children }) {
  return (
    <Card id={id} className="scroll-mt-20" data-testid={`guide-section-${id}`}>
      <div className="flex items-center gap-2.5 mb-3 pb-3 border-b border-brand-line">
        <div className="h-9 w-9 rounded-lg bg-brand-civic-50 flex items-center justify-center shrink-0">
          <Icon className="h-4.5 w-4.5 text-brand-civic" />
        </div>
        <h2 className="font-display font-bold text-brand-ink text-lg">{title}</h2>
      </div>
      <div className="text-sm text-slate-600 leading-relaxed space-y-2">{children}</div>
    </Card>
  );
}

const Step = ({ n, children }) => (
  <div className="flex gap-3">
    <span className="h-5 w-5 shrink-0 rounded-full bg-brand-navy text-white text-xs font-bold flex items-center justify-center">{n}</span>
    <span>{children}</span>
  </div>
);

const Term = ({ children }) => <span className="text-brand-navy font-semibold">{children}</span>;

export default function UserGuide() {
  const { user } = useAuth();
  const isManager = hasMinRole(user?.role, "manager");
  const isAnalyst = hasMinRole(user?.role, "analyst");

  const nav = [
    ["quick-start", "Quick Start", Rocket],
    ["roles", "Your Role", ShieldCheck],
    ["dashboard", "Dashboard", LayoutDashboard],
    ["bills", "Bills", FileText],
    ["search", "Global Search", Search],
    ["assignments", "Assignments", ClipboardList],
    ["analytics", "Analytics", BarChart3],
    ["hub", "Gov Info Hub", Landmark],
    ["alerts", "Alerts", Bell],
    ...(isAnalyst ? [["analysis", "Analysis & Reports", FileBarChart]] : []),
    ...(isManager ? [["manager", "Manager Tools", Users]] : []),
    ...(isManager ? [["admin", "Administration", Database]] : []),
    ["data", "Live vs Sample", Activity],
  ];

  return (
    <div>
      <PageHeader title="Help & User Guide" subtitle="A plain-English guide to using GovFlowState. Tailored to your role." />

      <div className="grid grid-cols-1 lg:grid-cols-[220px_1fr] gap-6">
        {/* Section nav */}
        <aside className="lg:sticky lg:top-20 h-max hidden lg:block" data-testid="guide-nav">
          <div className="rounded-xl border border-brand-line bg-white p-2 space-y-0.5 shadow-card">
            {nav.map(([id, label, Icon]) => (
              <a key={id} href={`#${id}`} data-testid={`guide-nav-${id}`}
                className="flex items-center gap-2.5 rounded-lg px-3 py-2 text-sm font-medium text-slate-600 hover:text-brand-navy hover:bg-brand-civic-50 transition-colors">
                <Icon className="h-4 w-4 text-brand-civic" /> {label}
              </a>
            ))}
          </div>
        </aside>

        <div className="space-y-4 max-w-3xl">
          <Section id="quick-start" icon={Rocket} title="Quick Start">
            <p className="text-slate-500">Get productive in two minutes.</p>
            <div className="space-y-2 mt-2">
              <Step n={1}><Term>Sign in</Term> with your email and password, or click Continue with Google.</Step>
              <Step n={2}>You land on the <Term>Dashboard</Term> — a snapshot of everything relevant to you.</Step>
              <Step n={3}>Use the <Term>search bar</Term> at the top of any page to find a bill, sponsor, or committee.</Step>
              <Step n={4}>Open <Term>Bills</Term> to browse and filter legislation; click any bill for full details.</Step>
              <Step n={5}>Check <Term>Assignments</Term> for your work and <Term>Alerts</Term> for new notifications.</Step>
            </div>
          </Section>

          <Section id="roles" icon={ShieldCheck} title="Understanding Your Role">
            <p>What you can see and do depends on your role. You are signed in as{" "}
              <Term>{{ administrator: "Administrator", manager: "Manager", analyst: "Policy Analyst", viewer: "Viewer" }[user?.role] || user?.role}</Term>.</p>
            <div className="mt-3 rounded-lg border border-brand-line overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-500 text-xs uppercase border-b border-brand-line">
                  <tr><th className="text-left px-3 py-2 font-semibold">Role</th><th className="text-left px-3 py-2 font-semibold hidden sm:table-cell">Meaning</th><th className="text-left px-3 py-2 font-semibold">Can do</th></tr>
                </thead>
                <tbody className="divide-y divide-brand-line">
                  {ROLES.map(([r, m, d]) => (
                    <tr key={r} className={user && ({ administrator: "Administrator", manager: "Manager", analyst: "Policy Analyst", viewer: "Viewer" }[user.role] === r) ? "bg-brand-civic-50" : ""}>
                      <td className="px-3 py-2 text-brand-ink font-semibold whitespace-nowrap">{r}</td>
                      <td className="px-3 py-2 text-slate-500 hidden sm:table-cell whitespace-nowrap">{m}</td>
                      <td className="px-3 py-2 text-slate-600">{d}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-slate-400 text-xs mt-2">New accounts start as Viewer. Your role appears under your name in the sidebar.</p>
          </Section>

          <Section id="dashboard" icon={LayoutDashboard} title="The Dashboard">
            <p>Your home screen shows, at a glance:</p>
            <ul className="list-disc pl-5 space-y-1 text-slate-600">
              <li><Term>Stat cards</Term>: total bills, high-priority, your assignments, in committee, passed, and unread alerts.</li>
              <li><Term>Assigned to me</Term>, <Term>high-priority bills</Term>, and <Term>recent legislative actions</Term>.</li>
              <li>Upcoming <Term>committee events</Term> and <Term>deadlines</Term>.</li>
              <li>Recent <Term>amendments</Term>, <Term>watched bills</Term>, <Term>documents</Term>, and <Term>alerts</Term>.</li>
            </ul>
            <p className="text-slate-600">Click <Term>View all</Term> on any panel to open the full list.</p>
          </Section>

          <Section id="bills" icon={FileText} title="Bills">
            <p>The Bills page lists all legislation you can access.</p>
            <ul className="list-disc pl-5 space-y-1 text-slate-600">
              <li><Term>Filter</Term> by keyword, status, chamber, priority, policy area, session, and source (Live vs Sample).</li>
              <li>Active filters show as chips — click one to remove it, or Clear all to reset.</li>
              <li>Click a bill number or title to open its full record.</li>
            </ul>
            <p className="font-semibold text-brand-ink mt-2">Inside a bill, you'll find tabs for:</p>
            <p className="text-slate-600">Overview, Text, Amendments, Votes, Activity, Documents, Assignments{isAnalyst ? ", and Analysis" : ""}.</p>
            <p className="text-slate-600 flex items-center gap-1.5"><Star className="h-3.5 w-3.5 text-brand-gold" /> Use <Term>Watch</Term> to add a bill to your dashboard watchlist, or follow the <Term>Source</Term> link to the official page.</p>
          </Section>

          <Section id="search" icon={Search} title="Global Search">
            <p>Use the search bar at the top of any page, or open the full <Term>Search</Term> page.</p>
            <p className="text-slate-600">It looks across bills, amendments, committees, sponsors, government entities, documents, sessions, internal notes, and government information — grouped by type. Try a bill number, a sponsor's name, or any keyword.</p>
          </Section>

          <Section id="assignments" icon={ClipboardList} title="Assignments">
            <p>Track bill reviews on the Assignments page.</p>
            <ul className="list-disc pl-5 space-y-1 text-slate-600">
              <li>Toggle between <Term>My assignments</Term> and <Term>All</Term>.</li>
              <li>Each row shows the bill, priority, assignee, and due date.</li>
              <li>Update the <Term>status</Term> (assigned, in progress, completed, blocked) and <Term>review status</Term> with the dropdowns.</li>
            </ul>
          </Section>

          <Section id="analytics" icon={BarChart3} title="Analytics">
            <p>Turns the current session into visuals:</p>
            <ul className="list-disc pl-5 space-y-1 text-slate-600">
              <li>Bill counts by status (bar chart).</li>
              <li>Activity by policy area (pie chart).</li>
              <li>A session-at-a-glance summary of key totals.</li>
            </ul>
          </Section>

          <Section id="hub" icon={Landmark} title="Government Information Hub">
            <p>A unified feed of official information beyond bills — press releases, notices, schedules, budget publications, and revenue materials.</p>
            <ul className="list-disc pl-5 space-y-1 text-slate-600">
              <li>Filter by category with the pill buttons at the top.</li>
              <li>Every item shows its source and publication date, and links to the original.</li>
            </ul>
          </Section>

          <Section id="alerts" icon={Bell} title="Alerts & Notifications">
            <p>The Alerts page and the bell icon keep you informed.</p>
            <ul className="list-disc pl-5 space-y-1 text-slate-600">
              <li>The <Term>Inbox</Term> lists notifications about your assignments and legislative activity.</li>
              <li>Unread items are highlighted — click Mark read, or Mark all read.</li>
              <li>Click <Term>Open</Term> to jump to the related item. The bell shows your unread count.</li>
            </ul>
          </Section>

          {isAnalyst && (
            <Section id="analysis" icon={FileBarChart} title="Analysis, Activity & Reports">
              <p className="text-slate-400 text-xs uppercase tracking-wider font-semibold">Policy Analyst and above</p>
              <p className="font-semibold text-brand-ink flex items-center gap-1.5"><GitPullRequestArrow className="h-4 w-4 text-brand-civic" /> Internal Analysis</p>
              <p className="text-slate-600">On a bill's Analysis tab, record assessments with a note type, priority flag, tracking status, and review status, plus a recommendation and internal reference. Notes are timestamped, attributed, and revision-tracked.</p>
              <p className="font-semibold text-brand-ink mt-2">Recording Activity</p>
              <p className="text-slate-600">Click Activity on a bill to log an action (referral, committee action, amendment, vote, passage, veto, calendar event). It appears on the bill's timeline.</p>
              <p className="font-semibold text-brand-ink mt-2">Reports</p>
              <p className="text-slate-600">On the Reports page, pick a report to preview it, then export to CSV with the download button. (PDF export is planned.)</p>
            </Section>
          )}

          {isManager && (
            <Section id="manager" icon={Users} title="Manager Tools">
              <p className="text-slate-400 text-xs uppercase tracking-wider font-semibold">Manager and above</p>
              <p className="font-semibold text-brand-ink">Assigning Bills to Staff</p>
              <p className="text-slate-600">On a bill, click <Term>Assign</Term>, then choose the staff member, assignment role (primary, secondary, policy area, manager), basis, due date, and optional notes. The assignee is notified.</p>
              <p className="font-semibold text-brand-ink mt-2">Managing Alert Rules</p>
              <p className="text-slate-600">On the Alerts page, the <Term>Alert Rules</Term> tab lets you automate notifications: name the rule, pick event types, optionally filter by priority, and choose recipient role and channels (in-app now; email via Resend). Teams delivery is planned.</p>
            </Section>
          )}

          {isManager && (
            <Section id="admin" icon={Database} title="Administration Panel">
              <p className="text-slate-400 text-xs uppercase tracking-wider font-semibold">Manager and above · some controls are Administrator-only</p>
              <ul className="space-y-2 text-slate-600">
                <li className="flex gap-2"><Users className="h-4 w-4 text-brand-civic shrink-0 mt-0.5" /><span><Term>Users</Term> — view everyone; admins add users, change roles, and enable/disable accounts.</span></li>
                <li className="flex gap-2"><Layers className="h-4 w-4 text-brand-civic shrink-0 mt-0.5" /><span><Term>Reference Data</Term> — manage Policy Units/Groups, Policy Areas, Committees, and Sessions.</span></li>
                <li className="flex gap-2"><Database className="h-4 w-4 text-brand-civic shrink-0 mt-0.5" /><span><Term>Data Sources</Term> — the Live/Sample toggle and government connectors; admins enable, disable, and Run now. The system never invents data; blocked sources are marked DEGRADED.</span></li>
                <li className="flex gap-2"><Building2 className="h-4 w-4 text-brand-civic shrink-0 mt-0.5" /><span><Term>Organization</Term> — name, brand, jurisdiction, color, and logo (multi-tenant).</span></li>
                <li className="flex gap-2"><ScrollText className="h-4 w-4 text-brand-civic shrink-0 mt-0.5" /><span><Term>Audit Log</Term> — a filterable record of who did what.</span></li>
                <li className="flex gap-2"><Activity className="h-4 w-4 text-brand-civic shrink-0 mt-0.5" /><span><Term>System Health</Term> — database status, Live vs Sample counts, and collection totals.</span></li>
              </ul>
            </Section>
          )}

          <Section id="data" icon={Activity} title="Live vs Sample Data">
            <p>A small <Term>Source</Term> badge appears on bills and information items:</p>
            <ul className="list-disc pl-5 space-y-1 text-slate-600">
              <li><span className="text-brand-teal font-semibold">Live</span> — real data from official Florida government sources.</li>
              <li><span className="text-brand-civic font-semibold">Sample</span> — demonstration data, kept strictly separate and never shown as real legislation.</li>
            </ul>
            <p className="text-slate-600">Production runs in Live mode by default. Only Administrators can change the data mode.</p>
          </Section>

          <Card className="border-brand-gold/30 bg-brand-gold-50" data-testid="guide-help">
            <div className="flex items-center gap-2.5">
              <HelpCircle className="h-5 w-5 text-brand-gold shrink-0" />
              <p className="text-sm text-slate-700">Need access you don't have, or something isn't working? Contact your <Term>Administrator</Term> — they manage roles, users, and data sources from the Administration panel.</p>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}
